import { create } from 'zustand';
import { safeStorage } from '../lib/storage';

export type ThemePref = 'system' | 'light' | 'dark';
const KEY = 'xq.cabinet.theme';

function readPref(): ThemePref {
  const v = safeStorage.get(KEY);
  return v === 'light' || v === 'dark' ? v : 'system';
}

/** `data-theme` on <html> is set only for an explicit choice; otherwise CSS follows prefers-color-scheme. */
export function applyTheme(pref: ThemePref) {
  const el = document.documentElement;
  if (pref === 'system') el.removeAttribute('data-theme');
  else el.setAttribute('data-theme', pref);
}

export function systemPrefersDark(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches;
}

interface ThemeState {
  pref: ThemePref;
  set: (p: ThemePref) => void;
  /** light ↔ dark relative to what is on screen now. */
  toggle: () => void;
}

export const useTheme = create<ThemeState>((set, get) => ({
  pref: readPref(),
  set(pref) {
    if (pref === 'system') safeStorage.remove(KEY);
    else safeStorage.set(KEY, pref);
    applyTheme(pref);
    set({ pref });
  },
  toggle() {
    const p = get().pref;
    const dark = p === 'dark' || (p === 'system' && systemPrefersDark());
    get().set(dark ? 'light' : 'dark');
  },
}));
